/**
 * @fileoverview EasingType (schema) → CSS timing-function map for in-screen
 * element animations.
 * @module screen-view-v2/animations/easing
 *
 * Intentional duplicate of the map in web-shell/src/engine/transitions/
 * transitionSpec.ts — screen-view-v2 is a standalone package and must not
 * import from the web-shell engine. Keep the two maps in sync when adding
 * curves.
 */

export const DEFAULT_EASING = 'ease-out';
/** Spring timing is degraded to a tween with mild overshoot on web */
export const SPRING_FALLBACK_DURATION_MS = 350;
export const SPRING_FALLBACK_EASING = 'cubic-bezier(0.34, 1.56, 0.64, 1)'; // easeOutBack

/** EasingType (schema) → CSS timing function. Penner curves as cubic-beziers. */
export const EASING_MAP: Record<string, string> = {
  linear: 'linear',
  ease: 'ease',
  easeIn: 'ease-in',
  easeOut: 'ease-out',
  easeInOut: 'ease-in-out',
  easeInSine: 'cubic-bezier(0.12, 0, 0.39, 0)',
  easeOutSine: 'cubic-bezier(0.61, 1, 0.88, 1)',
  easeInOutSine: 'cubic-bezier(0.37, 0, 0.63, 1)',
  easeInQuad: 'cubic-bezier(0.11, 0, 0.5, 0)',
  easeOutQuad: 'cubic-bezier(0.5, 1, 0.89, 1)',
  easeInOutQuad: 'cubic-bezier(0.45, 0, 0.55, 1)',
  easeInCubic: 'cubic-bezier(0.32, 0, 0.67, 0)',
  easeOutCubic: 'cubic-bezier(0.33, 1, 0.68, 1)',
  easeInOutCubic: 'cubic-bezier(0.65, 0, 0.35, 1)',
  easeInQuart: 'cubic-bezier(0.5, 0, 0.75, 0)',
  easeOutQuart: 'cubic-bezier(0.25, 1, 0.5, 1)',
  easeInOutQuart: 'cubic-bezier(0.76, 0, 0.24, 1)',
  easeInQuint: 'cubic-bezier(0.64, 0, 0.78, 0)',
  easeOutQuint: 'cubic-bezier(0.22, 1, 0.36, 1)',
  easeInOutQuint: 'cubic-bezier(0.83, 0, 0.17, 1)',
  easeInExpo: 'cubic-bezier(0.7, 0, 0.84, 0)',
  easeOutExpo: 'cubic-bezier(0.16, 1, 0.3, 1)',
  easeInOutExpo: 'cubic-bezier(0.87, 0, 0.13, 1)',
  easeInCirc: 'cubic-bezier(0.55, 0, 1, 0.45)',
  easeOutCirc: 'cubic-bezier(0, 0.55, 0.45, 1)',
  easeInOutCirc: 'cubic-bezier(0.85, 0, 0.15, 1)',
  easeInBack: 'cubic-bezier(0.36, 0, 0.66, -0.56)',
  easeOutBack: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  easeInOutBack: 'cubic-bezier(0.68, -0.6, 0.32, 1.6)',
};

/** Not expressible as cubic-bezier — degrade to 'ease' with a warning */
export const UNSUPPORTED_EASINGS = new Set([
  'easeInBounce', 'easeOutBounce', 'easeInOutBounce',
  'easeInElastic', 'easeOutElastic', 'easeInOutElastic',
]);

const warnedEasings = new Set<string>();

/**
 * Resolve a schema AnimationTiming to a CSS timing function.
 * Spring timing degrades to the easeOutBack tween.
 */
export function resolveEasing(
  timing: { type?: string; easing?: string } | null | undefined,
): string {
  if (timing?.type === 'spring') {
    return SPRING_FALLBACK_EASING;
  }
  const easing = timing?.easing;
  if (!easing) return DEFAULT_EASING;
  if (EASING_MAP[easing]) return EASING_MAP[easing];
  if (!warnedEasings.has(easing)) {
    warnedEasings.add(easing);
    const reason = UNSUPPORTED_EASINGS.has(easing)
      ? 'not expressible as cubic-bezier'
      : 'unknown EasingType';
    console.warn(`[ElementAnimation] Easing "${easing}" ${reason} — falling back to "ease"`);
  }
  return 'ease';
}
